import posthog from 'posthog-js'
import { DONEVERSE_ENV, DONEVERSE_RELEASE } from './env'

let initialized = false
let spaTrackingInitialized = false

function capturePageView() {
  posthog.capture('$pageview', {
    $current_url: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
  })
}

function initSpaPageviewTracking() {
  if (spaTrackingInitialized) return
  spaTrackingInitialized = true

  const capture = () => capturePageView()

  const originalPushState = history.pushState.bind(history)
  history.pushState = (...args) => {
    originalPushState(...args)
    capture()
  }

  const originalReplaceState = history.replaceState.bind(history)
  history.replaceState = (...args) => {
    originalReplaceState(...args)
    capture()
  }

  window.addEventListener('popstate', capture)
}

export function initPostHog() {
  if (initialized) return

  const apiKey = import.meta.env.VITE_POSTHOG_KEY
  const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

  if (!apiKey) {
    if (import.meta.env.DEV) {
      console.info('PostHog disabled (missing VITE_POSTHOG_KEY)')
    }
    return
  }

  posthog.init(apiKey, {
    api_host: apiHost,
    capture_pageview: false,
    capture_pageleave: true,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      // Handled errors are reported at their high-signal call sites. Capturing
      // every console.error would duplicate those events and turn routine
      // browser/extension noise into product incidents.
      capture_console_errors: false,
    },
    defaults: '2026-01-30',
  })

  // Tag every event with the deployment environment so stage and prod
  // events can be filtered apart in the same project, and with the build's
  // commit SHA so a session can be tied to a deployment.
  posthog.register({
    environment: DONEVERSE_ENV,
    ...(DONEVERSE_RELEASE ? { release: DONEVERSE_RELEASE } : {}),
  })

  capturePageView()
  initSpaPageviewTracking()

  initialized = true
  if (import.meta.env.DEV) {
    console.info('PostHog initialized')
  }
}

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!initialized) return
  posthog.identify(userId, traits)
}

export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (!initialized) return
  posthog.capture(event, properties)
}

export function captureException(error: unknown, properties?: Record<string, unknown>) {
  if (!initialized) return
  posthog.captureException(error, properties)
}

export function resetUser() {
  if (!initialized) return
  posthog.reset()
}

export { posthog }
